<template>
    <div v-if="visible" class="incident-wrapper">
        <div class="incident-card" :class="statusClass">

            <div class="top-bar"></div>

            <div class="header">
                <div class="title">
                    {{ isMaintenance ? "🛠 Maintenance" : "⚠ Incident" }} {{ incidentTitle }}
                </div>
            </div>

            <div class="badge">
                {{ statusLabel }}
            </div>

            <div class="section">
                DESCRIPTION
            </div>

            <div class="message" v-html="incidentMessage"></div>

            <div class="actions">
                <button class="close" @click="close">
                    Fermer
                </button>
            </div>

        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import { authClient } from "@/plugins/nhost"

interface ComponentItem {
    name: string
}

interface IncidentUpdate {
    id: string
    body: string
    affected_components?: ComponentItem[]
}

interface Incident {
    id: string
    name: string
    status: string
    incident_updates: IncidentUpdate[]
}

// Scheduled maintenances share the same shape as incidents in the Statuspage
// API (id/name/status/incident_updates), just with their own status values
// (scheduled/in_progress/verifying/completed) instead of the incident ones.
type ScheduledMaintenance = Incident

const visible = ref(false)

const incidentId = ref<string | null>(null)
const updateId = ref<string | null>(null)

const incidentTitle = ref("")
const incidentMessage = ref("")
const incidentStatus = ref("")
const isMaintenance = ref(false)

const allowedComponents = ["Dashboard", "Check-list"]

let interval: any = null
let initialized = false

const statusClass = computed(() => {
    switch (incidentStatus.value) {
        case "investigating": return "investigating"
        case "identified": return "identified"
        case "monitoring": return "monitoring"
        case "resolved": return "resolved"
        case "scheduled": return "scheduled"
        case "in_progress": return "in_progress"
        case "verifying": return "verifying"
        case "completed": return "completed"
        default: return "identified"
    }
})

const statusLabel = computed(() => {
    switch (incidentStatus.value) {
        case "investigating": return "INVESTIGATION"
        case "identified": return "IDENTIFIÉ"
        case "monitoring": return "MONITORING"
        case "resolved": return "RÉSOLU"
        case "scheduled": return "PROGRAMMÉE"
        case "in_progress": return "EN COURS"
        case "verifying": return "VÉRIFICATION"
        case "completed": return "TERMINÉE"
        default: return "INCIDENT"
    }
})

function matchesAllowedComponent(item: Incident | ScheduledMaintenance): boolean {
    return (item.incident_updates || []).some((update: IncidentUpdate) => {

        const comps = update.affected_components || []

        return comps.some((c: ComponentItem) =>
            allowedComponents.some(name =>
                c.name.toLowerCase().includes(name.toLowerCase())
            )
        )

    })
}

async function checkIncident() {

    try {

        const [incidentsRes, maintenancesRes] = await Promise.all([
            fetch("https://blaxtair.statuspage.io/api/v2/incidents/unresolved.json"),
            fetch("https://blaxtair.statuspage.io/api/v2/scheduled-maintenances/active.json"),
        ])

        const incidentsData = await incidentsRes.json()
        const maintenancesData = await maintenancesRes.json()

        const incident: Incident | undefined =
            (incidentsData.incidents || []).find(matchesAllowedComponent)

        // An ongoing incident takes priority over a maintenance notice when both exist.
        const maintenance: ScheduledMaintenance | undefined = incident
            ? undefined
            : (maintenancesData.scheduled_maintenances || []).find(matchesAllowedComponent)

        const item = incident || maintenance

        if (!item) {
            visible.value = false
            return
        }

        const lastUpdate = item.incident_updates[0]

        const storageKey =
            "incident_" + item.id + "_" + lastUpdate.id

        if (sessionStorage.getItem(storageKey)) return

        incidentId.value = item.id
        updateId.value = lastUpdate.id
        incidentTitle.value = item.name
        incidentMessage.value = lastUpdate.body
        incidentStatus.value = item.status
        isMaintenance.value = !incident

        visible.value = true

    } catch (e) {

        console.error(e)

    }

}

function close() {

    const key =
        "incident_" + incidentId.value + "_" + updateId.value

    sessionStorage.setItem(key, "1")

    visible.value = false

}

function startChecking() {

    stopChecking()

    checkIncident()

    interval = setInterval(checkIncident, 60000)

}

function stopChecking() {

    if (interval) {
        clearInterval(interval)
        interval = null
    }

}

onMounted(() => {

    authClient.onAuthStateChanged((state) => {

        if (state === "SIGNED_IN") {

            if (!initialized) {
                startChecking()
                initialized = true
            }

        } else {

            visible.value = false
            stopChecking()

            sessionStorage.clear()

            initialized = false

        }

    })

})

onBeforeUnmount(() => {
    stopChecking()
})
</script>

<style scoped>
/* Remonté au-dessus de la zone du bouton chatbot (fixed bottom-right, ~80px). */
.incident-wrapper {
    position: fixed;
    bottom: 112px;
    right: 40px;
    z-index: 999999;
    font-family: Inter, system-ui;
}

.incident-card {
    width: 420px;
    background: #121a2b;
    border-radius: 16px;
    padding: 26px;
    color: white;
    box-shadow: 0 25px 80px rgba(0, 0, 0, .45);
    position: relative;
}

.top-bar {
    position: absolute;
    left: 0;
    top: 0;
    height: 6px;
    width: 100%;
    border-radius: 16px 16px 0 0;
}

.header {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 14px;
}

.badge {
    display: inline-block;
    font-size: 12px;
    padding: 6px 10px;
    border-radius: 20px;
    margin-bottom: 18px;
    font-weight: 600;
    letter-spacing: .5px;
}

.section {
    font-size: 12px;
    opacity: .6;
    margin-bottom: 8px;
    letter-spacing: .5px;
}

.message {
    font-size: 14px;
    line-height: 1.6;
    opacity: .9;
    margin-bottom: 26px;
}

.actions {
    display: flex;
    justify-content: flex-end;
}

.close {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, .25);
    color: white;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    transition: .2s;
}

.close:hover {
    background: rgba(255, 255, 255, .08);
}

.investigating .top-bar {
    background: #ff9800;
}

.investigating .badge {
    background: rgba(255, 152, 0, .15);
    color: #ffb74d;
}

.identified .top-bar {
    background: #ff4d4f;
}

.identified .badge {
    background: rgba(255, 77, 79, .15);
    color: #ff7b7d;
}

.monitoring .top-bar {
    background: #3b82f6;
}

.monitoring .badge {
    background: rgba(59, 130, 246, .15);
    color: #7fb0ff;
}

.resolved .top-bar {
    background: #22c55e;
}

.resolved .badge {
    background: rgba(34, 197, 94, .15);
    color: #4ade80;
}

.scheduled .top-bar {
    background: #8b93a7;
}

.scheduled .badge {
    background: rgba(139, 147, 167, .15);
    color: #c3c9d6;
}

.in_progress .top-bar {
    background: #14b8a6;
}

.in_progress .badge {
    background: rgba(20, 184, 166, .15);
    color: #5eead4;
}

.verifying .top-bar {
    background: #a855f7;
}

.verifying .badge {
    background: rgba(168, 85, 247, .15);
    color: #d8b4fe;
}

.completed .top-bar {
    background: #22c55e;
}

.completed .badge {
    background: rgba(34, 197, 94, .15);
    color: #4ade80;
}
</style>